import { useEffect, useMemo, useState } from "react";
import { Edit, Plus, Power, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../../../services/api";
import { Table } from "../../../../components/table";
import { BackButton } from "../../../../components/button/back-button";
import { ConfirmationModal } from "../../../../components/confirmation-modal";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import type { AiSkill } from "../../../../types/ai-skill";

export default function ListAiSkills() {
  const { success, error } = useToast();
  const navigate = useNavigate();

  const [skills, setSkills] = useState<AiSkill[]>([]);
  const [skillToDelete, setSkillToDelete] = useState<AiSkill | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const enabledSkillsCount = useMemo(
    () => skills.filter((skill) => skill.enabled).length,
    [skills],
  );

  useEffect(() => {
    void getSkills();
  }, []);

  async function getSkills() {
    try {
      setIsLoading(true);
      const response = await api.get<AiSkill[]>("/ai-skills");
      setSkills(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
      setSkills([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function toggleEnabled(skill: AiSkill) {
    if (!skill.id || isUpdating) return;

    try {
      setIsUpdating(true);
      await api.patch(`/ai-skills/${skill.id}`, { enabled: !skill.enabled });
      success(skill.enabled ? "Skill desativada." : "Skill ativada.");
      await getSkills();
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsUpdating(false);
    }
  }

  async function deleteSkill(id: string) {
    if (isDeleting) return;

    try {
      setIsDeleting(true);
      await api.delete(`/ai-skills/${id}`);
      setSkillToDelete(null);
      success("Skill removida com sucesso.");
      await getSkills();
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsDeleting(false);
    }
  }

  const columns = [
    {
      key: "name",
      label: "Skill",
      render: (skill: AiSkill) => (
        <div>
          <p className="font-medium text-slate-900">{skill.name}</p>
          <p className="mt-1 break-all text-xs text-slate-500">
            {skill.slug}
          </p>
        </div>
      ),
    },
    {
      key: "relatedIntents",
      label: "Intenções",
      render: (skill: AiSkill) =>
        skill.relatedIntents?.length
          ? skill.relatedIntents
              .map((intent) => intent.name || intent.slug)
              .join(", ")
          : "-",
    },
    {
      key: "functions",
      label: "Functions",
      render: (skill: AiSkill) => skill.functions?.length ?? 0,
    },
    {
      key: "enabled",
      label: "Status",
      render: (skill: AiSkill) => (
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
            skill.enabled
              ? "bg-emerald-50 text-emerald-700"
              : "bg-slate-100 text-slate-600"
          }`}
        >
          {skill.enabled ? "Ativa" : "Inativa"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Ações",
      render: (skill: AiSkill) => (
        <div className="text-center flex justify-center gap-3">
          <button
            type="button"
            onClick={() =>
              navigate(`/painel/config/ai-skills/editar/${skill.id}`)
            }
            className="btn-link-primary cursor-pointer"
            disabled={!skill.id}
          >
            <Edit className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={() => toggleEnabled(skill)}
            className={
              skill.enabled
                ? "btn-link-danger cursor-pointer"
                : "btn-link-success cursor-pointer"
            }
            disabled={!skill.id || isUpdating}
          >
            <Power className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={() => setSkillToDelete(skill)}
            className="btn-link-danger cursor-pointer"
            disabled={!skill.id}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="app-page">
      <BackButton />

      <div className="flex flex-col gap-3 mb-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="app-page-title">Skills de IA</h1>
          <p className="app-page-subtitle">
            {enabledSkillsCount} ativas de {skills.length} cadastradas
          </p>
        </div>

        <button
          onClick={() => navigate("/painel/config/ai-skills/cadastro")}
          className="btn btn-neutral cursor-pointer"
        >
          <Plus className="w-4 h-4" /> Nova Skill
        </button>
      </div>

      <Table<AiSkill>
        columns={columns}
        data={skills}
        emptyMessage={
          isLoading ? "Carregando skills..." : "Nenhuma skill cadastrada."
        }
      />

      <ConfirmationModal
        isOpen={!!skillToDelete}
        title="Confirmar exclusão"
        description={
          skillToDelete
            ? `Tem certeza que deseja excluir a skill ${skillToDelete.name}?`
            : ""
        }
        onCancel={() => setSkillToDelete(null)}
        onConfirm={() => skillToDelete?.id && deleteSkill(skillToDelete.id)}
        isLoading={isDeleting}
        confirmLabel="Excluir"
      />
    </div>
  );
}
